import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import { components as ReactSelectComponents } from 'react-select'
import type { UI2SelectFieldProps } from 'shared-libs/components/ui2/fields/ui2-select-field/ui2-select-field-props'
import { UI2_SELECT_FIELD_DEFAULT_PROPS } from 'shared-libs/components/ui2/fields/ui2-select-field/ui2-select-field-props'
import {
  SelectOption,
  getEffectiveTranslationTable,
  getOptionLabel,
  getSelectedOptions,
  getStoredValueForOption,
  resolveSelectOptions,
} from 'shared-libs/components/ui2/fields/core-field/selectable-field-props'
import { ViewRenderer } from 'shared-libs/components/view/renderer'

import { ReactSelect } from 'browser/components/atomic-elements/atoms/select/select/react-select'
import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'

import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import { UI2PulseHighlight, IUI2PulseHighlightRef } from 'browser/components/ui2/behavior/pulse-highlight/pulse-highlight'
import './_ui2-select-field.scss'

type MultiMode = 'checkbox' | 'single'

function hasMeaningfulInitialValue(value: any): boolean {
  if (_.isNil(value)) return false
  if (value === '') return false
  if (_.isArray(value) && value.length === 0) return false
  return true
}

interface IUI2SelectWebExtras {
  onChange?: (value: any) => void
  errors?: any[]
  className?: string
  menuIsOpen?: boolean
  styles?: any
  onOpen?: () => void
  inputValue?: string
  onInputChange?: (value: string, actionMeta?: any) => void
  isHorizontalLayout?: boolean
}

/**
 * @uiComponent
 */
export interface IUI2SelectFieldProps extends UI2SelectFieldProps, IUI2SelectWebExtras {}

interface IUI2SelectFieldState {
  internalValue: any
  isMenuOpen: boolean
}

export class UI2SelectField extends React.Component<IUI2SelectFieldProps, IUI2SelectFieldState> {
  public static defaultProps: Partial<IUI2SelectFieldProps> = {
    ...UI2_SELECT_FIELD_DEFAULT_PROPS,
  }

  private readonly hadInitialValue: boolean
  private static instanceCounter = 0
  private readonly instanceClass: string
  private pulseRef = React.createRef<IUI2PulseHighlightRef>()

  constructor(props: IUI2SelectFieldProps) {
    super(props)
    this.hadInitialValue = hasMeaningfulInitialValue(props.value)
    UI2SelectField.instanceCounter += 1
    this.instanceClass = `ui2-selectField--i${UI2SelectField.instanceCounter}`
    this.state = {
      internalValue: props.value,
      isMenuOpen: !!props.menuIsOpen,
    }
  }

  public componentDidMount() {
    if (this.props.menuIsOpen === undefined) {
      document.addEventListener('mousedown', this.handleDocumentMouseDown, true)
    }
  }

  public componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleDocumentMouseDown, true)
  }

  public UNSAFE_componentWillReceiveProps(next: IUI2SelectFieldProps) {
    if (next.value !== this.props.value) {
      this.setState({ internalValue: next.value })
    }
    if (next.menuIsOpen !== undefined && next.menuIsOpen !== this.props.menuIsOpen) {
      this.setState({ isMenuOpen: !!next.menuIsOpen })
    }
  }

  private handleDocumentMouseDown = (e: MouseEvent) => {
    if (!this.state.isMenuOpen) return
    const target = e.target as HTMLElement | null
    if (!target) return
    if (target.closest?.('.' + this.instanceClass)) return
    if (target.closest?.('.reactSelect__menu') || target.closest?.('.reactSelect__menu-portal')) return
    this.setState({ isMenuOpen: false })
  }

  private handleMenuOpen = () => {
    if (this.props.menuIsOpen === undefined) {
      this.setState({ isMenuOpen: true })
    }
  }

  private handleMenuClose = () => {
    if (this.props.menuIsOpen === undefined) {
      this.setState({ isMenuOpen: false })
    }
  }

  private getEffectivePlaceholder(): string {
    const { placeholder, label } = this.props
    if (placeholder) return placeholder
    return label ? `Select ${label}` : 'Select'
  }

  public render(): React.ReactNode {
    const { className } = this.props
    const options = resolveSelectOptions(this.props)
    const effectiveEnablePrefilled = this.props.enablePrefilledMode && this.hadInitialValue

    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        enablePrefilledMode={effectiveEnablePrefilled}
        className={classNames('ui2-selectField', this.instanceClass, className)}
        renderReadOnly={(_, helpers) => this.renderReadOnly(options, helpers)}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
        pulseRef={this.pulseRef}
      >
        <UI2FieldControl>
          <UI2PulseHighlight ref={this.pulseRef}>
            {this.renderSelect(options, effectiveEnablePrefilled)}
          </UI2PulseHighlight>
        </UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private renderSelect(options: SelectOption[], effectiveEnablePrefilled: boolean): React.ReactNode {
    const {
      isDisabled, isSearchable, isMultiSelect,
      multiMode, allowSelectAll, isClearable, menuIsOpen, autoFocus,
      noOptionsMessage, optionTemplate, isHorizontalLayout, styles, onOpen,
      inputValue, onInputChange,
    } = this.props

    const effectivePlaceholder = this.getEffectivePlaceholder()
    // Suppress the clear ×  in prefilled mode — the Cancel affordance handles
    // reversion and showing both creates two cancel affordances.
    const effectiveClearable = isClearable && !effectiveEnablePrefilled
    const isCreatable = this.isCreatableAllowed()
    const effectiveMultiMode: MultiMode | undefined = isMultiSelect ? (multiMode ?? 'checkbox') : undefined
    const reactSelectMultiMode = effectiveMultiMode === 'checkbox' ? 'checkbox' : (isMultiSelect ? 'instant' : undefined)
    const effectiveMenuIsOpen = menuIsOpen !== undefined ? menuIsOpen : this.state.isMenuOpen
    const translatedOptions = this.getTranslatedOptions(options)

    return (
      <ReactSelect
        className='ui2-selectField-select'
        options={translatedOptions}
        value={this.state.internalValue}
        onChange={this.handleChange}
        optionLabelPath='label'
        optionValuePath='value'
        multi={!!isMultiSelect}
        multiMode={reactSelectMultiMode as any}
        allowSelectAll={!!allowSelectAll}
        isCreatable={isCreatable}
        isClearable={effectiveClearable}
        clearable={effectiveClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        isHorizontalLayout={isHorizontalLayout}
        placeholder={effectivePlaceholder}
        menuIsOpen={effectiveMenuIsOpen}
        onOpen={() => {
          this.handleMenuOpen()
          onOpen?.()
        }}
        onMenuClose={this.handleMenuClose}
        styles={styles}
        inputValue={inputValue}
        onInputChange={onInputChange}
        autoFocus={autoFocus}
        noOptionsMessage={noOptionsMessage}
        onNewOptionClick={isCreatable ? this.handleCreateNewOption : undefined}
        optionRenderer={optionTemplate ? this.buildOptionRenderer(optionTemplate) : undefined}
        frames={this.props.frames}
      />
    )
  }

  private renderReadOnly(options: SelectOption[], { editNode }: { editNode?: React.ReactNode } = {}): React.ReactNode {
    const { value, isMultiSelect } = this.props
    const translationTable = getEffectiveTranslationTable(this.props)
    const selected = getSelectedOptions(options, value, isMultiSelect)
    let labels: string[]
    if (selected.length) {
      labels = selected.map((o) => getOptionLabel(o, translationTable))
    } else if (isMultiSelect && _.isArray(value)) {
      labels = value.map((v) => this.rawValueLabel(v)).filter(Boolean)
    } else if (!_.isNil(value) && value !== '') {
      const fallback = this.rawValueLabel(value)
      labels = fallback ? [fallback] : []
    } else {
      labels = []
    }
    const content = !labels.length
      ? <UI2NoValueAvailable className='ui2-selectField-readOnlyText' />
      : <span className='ui2-selectField-readOnlyText'>{labels.join(', ')}</span>
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </>
    )
  }

  private rawValueLabel(v: any): string {
    if (v && typeof v === 'object') {
      return v.label ?? v.displayName ?? String(v.value ?? '')
    }
    return String(v ?? '')
  }

  private getTranslatedOptions(options: SelectOption[]): SelectOption[] {
    const translationTable = getEffectiveTranslationTable(this.props)
    if (_.isEmpty(translationTable)) return options
    return options.map((o) => ({ ...o, label: getOptionLabel(o, translationTable) }))
  }

  private handleChange = (newValue: any) => {
    const { isMultiSelect, onChange } = this.props
    const options = resolveSelectOptions(this.props)
    let writable: any
    if (isMultiSelect) {
      const arr = _.isArray(newValue) ? newValue : []
      writable = arr.map((opt) => this.toStoredValue(opt, options))
    } else if (_.isNil(newValue)) {
      writable = null
    } else {
      writable = this.toStoredValue(newValue, options)
    }
    this.setState({ internalValue: writable })
    onChange?.(writable)
  }

  private handleCreateNewOption = (label: string): void => {
    if (!label) return
    const newOption: SelectOption = { value: label, label }
    if (this.props.isMultiSelect) {
      const current = _.isArray(this.state.internalValue) ? this.state.internalValue : []
      this.handleChange([...current, newOption])
    } else {
      this.handleChange(newOption)
    }
    this.pulseRef.current?.pulse()
  }

  private toStoredValue(picked: any, options: SelectOption[]): any {
    if (picked == null) return picked
    const rawValue = typeof picked === 'object' && 'value' in picked ? picked.value : picked
    const match = options.find((o) => o.value === rawValue)
    const option: SelectOption = match
      ? match
      : { value: rawValue, label: picked?.label ?? String(rawValue ?? '') }
    return getStoredValueForOption(option, this.props)
  }

  private cachedOptionRenderer: { schema: any; component: React.FC<any> } | null = null

  private buildOptionRenderer(optionTemplate: any): React.FC<any> {
    if (this.cachedOptionRenderer && this.cachedOptionRenderer.schema === optionTemplate) {
      return this.cachedOptionRenderer.component
    }
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const ComponentsMap = require('browser/components').default
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const apis = require('browser/app/models/apis').default

    const Renderer: React.FC<any> = (optionProps: any) => {
      const { frames } = this.props
      const data = optionProps?.data
      const optionValue = data && typeof data === 'object' && 'value' in data ? data.value : data
      const optionState = data && typeof data === 'object' ? data : optionValue
      return (
        <ReactSelectComponents.Option {...optionProps}>
          <div className='ui2-selectField-optionContent'>
            <ViewRenderer
              schema={optionTemplate}
              state={{ components: ComponentsMap, option: optionState, frames }}
              componentsMap={ComponentsMap}
              api={apis}
            />
          </div>
        </ReactSelectComponents.Option>
      )
    }
    this.cachedOptionRenderer = { schema: optionTemplate, component: Renderer }
    return Renderer
  }

  private isCreatableAllowed(): boolean {
    if (!this.props.isCreatable) return false
    if (!_.isEmpty(this.props.options)) return true
    const dataSchema = this.props.frames?.getContext?.('dataSchema')
    const itemSchema =
      dataSchema?.type === 'array' && dataSchema.items ? dataSchema.items : dataSchema
    if (!_.isEmpty(itemSchema?.suggestions)) return true
    return false
  }
}
